import React, { useEffect, useState, useMemo } from 'react'
import { getJson } from '../../../utils/http'
import { useTranslation } from 'react-i18next'
import dayjs from 'dayjs'
import { ModalFilterPlanByCalendar } from './ModalFilterPlanByCalendar'
import { IconCalendar, IconChildren, IconHumanFill, IconArrowDown } from './Icon'
import { DataSearchType, searchData, KeyDataSearch } from './type'
import { buildAvailabilityUrl, buildCheckInDateParams, buildCheckInDateUrl, deleteFallbackGuardParams } from './utils'
import { DatePicker } from '../../date-picker'
import { DataType } from '../../date-picker/types'
import { stayDayOptions } from '../../search-bar/utils'
import { usePlanStore } from '../../../store/planStore'
import { useLpAvailabilityPageStore } from '../../../store/lpAvailabilityPageStore'

type Props = {
  onChange?: () => void
}

export const SearchCalendar = (props: Props) => {
  const [filterData, setFilterData] = useState<DataSearchType>({ ...searchData })
  const [checkInDates, setCheckInDates] = useState<DataType[]>([])
  const [inputType, setInputType] = useState<'peopleCount' | 'stayDay'>('peopleCount')
  // URLにこども関連パラメータがあるか、またはモーダルで明示的に設定されたかを追跡
  const [childrenExplicitlySet, setChildrenExplicitlySet] = useState<boolean>(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return (
      urlParams.has('number_of_study_abroad') ||
      urlParams.has('number_of_preschoolers_1') ||
      urlParams.has('number_of_students_1')
    )
  })
  const { t } = useTranslation()
  const { openModalFilter, setOpenModalFilter } = usePlanStore()
  const { isJpe } = useLpAvailabilityPageStore()
  const allergyCondition = (document.querySelector('#default_class_capacity_allergy') as HTMLInputElement)?.value == 'true'
  const schoolExperienceCondition =
    (document.querySelector('#default_class_capacity_school_experience') as HTMLInputElement)?.value == 'true'
  const checkInDateUrl = buildCheckInDateUrl(window.location.pathname)

  // 初回 mount で1度だけ window.location.search から params を組み立てて取得する。
  // モーダル経由の検索条件変更は redirectToAvailabilityWithQuery でページ遷移 (or onChange コールバック)
  // させる設計のため、deps を [filterData] 等に拡張すると二重 fetch になる (issue #1614)。
  // ※ 将来 URL を pushState で書き換える SPA 構造に変える場合は、popstate を listen するか
  // この useEffect を window.location.search 依存にして再 fetch する必要があります。
  useEffect(() => {
    if (!checkInDateUrl) return
    getJson(checkInDateUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      params: buildCheckInDateParams(window.location.search, filterData),
    })
      .then((data) => {
        if (!data) return
        setCheckInDates((data.check_in_dates as DataType[]) ?? [])
        return
      })
      .catch((_e) => undefined)
  }, [])

  const fromDateText = useMemo(() => {
    if (filterData.start_date) {
      return dayjs(filterData.start_date).format('M/D')
    }
    const urlParams = new URLSearchParams(window.location.search)
    const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()[0]?.replace('/', '-') // "2025/08,2025/06,2025/10"
    if (selectedMonth) {
      const selectedDate = checkInDates.find((date) => date.date.startsWith(selectedMonth))
      if (selectedDate) {
        return dayjs(selectedDate.date).format('M/D')
      }
    }
    if (checkInDates?.[0]) {
      return dayjs(checkInDates[0].date).format('M/D')
    }
    return ''
  }, [filterData.start_date, checkInDates])

  const toDateText = useMemo(() => {
    if (filterData.start_date && filterData.stay_day) {
      const stayDays = parseInt(filterData.stay_day)
      return dayjs(filterData.start_date)
        .add(stayDays - 1, 'day')
        .format('M/D')
    }
    return ''
  }, [filterData.start_date, filterData.stay_day])

  const STAY_DAY_OPTIONS = useMemo(() => stayDayOptions(t), [t])

  useEffect(() => {
    if (open) {
      const queryParams = new URLSearchParams(window.location.search)
      const tempObject: DataSearchType = { ...searchData }
      const arrayKeys = ['stay_day', 'birthdays']
      const numberKeys = ['number_of_adults', 'number_of_preschoolers_1', 'number_of_students_1', 'number_of_study_abroad']
      const keySearch = Object.keys(searchData)

      queryParams.forEach((value, key) => {
        if (keySearch.includes(key)) {
          if (arrayKeys.includes(key)) {
            tempObject[key] = value ? value : ''
            if (key === 'birthdays') {
              const birthdayStrings = value.split(',')
              const birthdays = birthdayStrings.map((dateStr) => {
                const [year, month, day] = dateStr.split('-')
                return {
                  year: year || '',
                  month: month || '',
                  day: day || '',
                }
              })
              tempObject.birthdays = birthdays
            }
          } else if (numberKeys.includes(key)) {
            tempObject[key] = Number(value)
          } else {
            tempObject[key] = value
          }
        }
      })
      // 留学ありは最低1人必要
      if (tempObject.number_of_study_abroad < 1) {
        tempObject.number_of_study_abroad = 1
        if (!tempObject.birthdays || tempObject.birthdays.length < 1) {
          tempObject.birthdays = [{ year: '', month: '', day: '' }]
        }
      }

      setFilterData(tempObject)
    }
  }, [open, window])

  const handleGenerateSearchQueryURL = (currentKeySearch: string, data: DataSearchType, forceIncludeChildren = false) => {
    const includeChildren = forceIncludeChildren || childrenExplicitlySet
    const query: Record<string, string> = {
      number_of_adults: String(data.number_of_adults),
      stay_day: data.stay_day,
      start_date: data.start_date ? dayjs(data.start_date).format('YYYY-MM-DD') : '',
    }
    // こどもパラメータはユーザーが明示的に設定した場合のみURLに含める
    if (includeChildren) {
      query.number_of_preschoolers_1 = String(data.number_of_preschoolers_1)
      query.number_of_students_1 = String(data.number_of_students_1)
      query.number_of_study_abroad = String(data.number_of_study_abroad)
      query.birthdays = data.birthdays
        .filter((birthday) => birthday.year && birthday.month && birthday.day)
        .map((birthday) => `${birthday.year}-${birthday.month}-${birthday.day}`)
        .join(',')
    }
    // [issue#2436] ループガード用のパラメータは検索条件ではないため、条件変更時に捨てる。
    const currentQueryParams = deleteFallbackGuardParams(new URLSearchParams(currentKeySearch))
    Object.keys(query).forEach((key) => {
      if (query[key] && !['false'].includes(query[key])) {
        currentQueryParams.set(key, query[key])
      } else {
        currentQueryParams.delete(key)
      }
    })
    // こどもパラメータ未指定の場合はURLから削除
    if (!includeChildren) {
      ;['number_of_preschoolers_1', 'number_of_students_1', 'number_of_study_abroad', 'birthdays'].forEach((key) => {
        currentQueryParams.delete(key)
      })
    }
    if (currentQueryParams.get('page')) {
      currentQueryParams.set('page', '1')
    }
    return currentQueryParams.toString()
  }

  const handleChangeDataSearch = (data: DataSearchType, checkInDates: DataType[]) => {
    setFilterData(data)
    setCheckInDates(checkInDates)
    setChildrenExplicitlySet(true)
    setOpenModalFilter(false)
    const currentKeySearch = window.location.search
    const queryURL = handleGenerateSearchQueryURL(currentKeySearch, data, true)
    redirectToAvailabilityWithQuery(queryURL)
  }

  const handleChangeFromDate = (key: KeyDataSearch, value: number | string, _type?: 'down' | 'up' | 'option' | 'text') => {
    const selectedDate = value ? dayjs(value).format('YYYY-MM-DD') : undefined
    const newStartDate = selectedDate === filterData.start_date ? '' : selectedDate
    setFilterData((prevDataSearch) => ({ ...prevDataSearch, start_date: newStartDate }))

    const currentKeySearch = window.location.search
    const queryURL = handleGenerateSearchQueryURL(currentKeySearch, {
      ...filterData,
      start_date: newStartDate,
    })
    redirectToAvailabilityWithQuery(queryURL)
  }

  const redirectToAvailabilityWithQuery = (queryURL: string) => {
    const pathname = window.location.pathname
    const isAvailabilityPage = pathname.endsWith('/availability')

    if (isAvailabilityPage) {
      history.pushState({}, '', queryURL ? `?${queryURL}` : pathname)
      props.onChange?.()
    } else {
      const finalURL = `${buildAvailabilityUrl(pathname)}${queryURL ? `?${queryURL}` : ''}`
      window.location.href = finalURL
    }
  }

  // パラメータ未指定時はサービスタイプに基づきデフォルト1名を表示
  const getTotalChildrenCount = () => {
    const total = Math.max(
      filterData.number_of_preschoolers_1 + filterData.number_of_students_1 + filterData.number_of_study_abroad,
      1,
    )
    return total
  }

  // Get stay day text
  const getStayDayText = () => {
    return STAY_DAY_OPTIONS.find((option) => option.value === filterData.stay_day)?.name || ''
  }

  // Mobile view component
  const SearchComponent = () => {
    return (
      <div className="w-full rounded-sm search-component py-2 md:py-4">
        <div className="title-wrapper">
          <div className={`title ${isJpe ? '!text-left' : 'text-center'}`}>{t('landing_page.search_calendar.title')}</div>
          {isJpe && <div className="subtitle">{t('landing_page.search_calendar.subtitle')}</div>}
        </div>
        <div
          className="flex items-center justify-between gap-2"
          onClick={() => {
            setOpenModalFilter(true)
            setInputType('peopleCount')
          }}
        >
          <div className="item-options w-1/2">
            <div className="flex items-center">
              <div className="mr-2 flex items-center gap-1">
                <IconHumanFill />
                <span className="text-primary font-bold">{t('landing_page.search_calendar.adults')}</span>
              </div>
              <div>
                <span className="text-gray-500">
                  {filterData.number_of_adults}
                  {t('reservation_confirm.modal_booking.people')}
                </span>
              </div>
            </div>
            {isJpe && <IconArrowDown className="ml-auto shrink-0" color="#8B9096" />}
            {!isJpe && (
              <button className="rounded-full px-2 py-1 text-white font-size-12 font-bold background-green-primary">
                {t('landing_page.search_calendar.txt_change')}
              </button>
            )}
          </div>

          <div
            className="item-options w-1/2"
            onClick={() => {
              setOpenModalFilter(true)
              setInputType('peopleCount')
            }}
          >
            <div className="flex items-center">
              <div className="mr-2 flex items-center gap-1">
                <IconChildren />
                <span className="text-primary font-bold">{t('landing_page.search_calendar.children')}</span>
              </div>
              <div>
                <span className="text-gray-500">
                  {getTotalChildrenCount()}
                  {t('reservation_confirm.modal_booking.people')}
                </span>
              </div>
            </div>
            {isJpe && <IconArrowDown className="ml-auto shrink-0" color="#8B9096" />}
            {!isJpe && (
              <button className="rounded-full px-2 py-1 text-white font-size-12 font-bold background-green-primary">
                {t('landing_page.search_calendar.txt_change')}
              </button>
            )}
          </div>
        </div>

        {/* Third row - Date */}
        <div
          className="item-options w-full"
          onClick={() => {
            setOpenModalFilter(true)
            setInputType('stayDay')
          }}
        >
          <div className="flex items-center">
            <div className="mr-2 flex items-center gap-2">
              <IconCalendar color="#189855" />
              <span className="text-primary font-bold">{t('landing_page.search_calendar.stay_period')}</span>
            </div>
            <div className="text-gray-500">
              {fromDateText && toDateText && (
                <span>
                  ({fromDateText} ~ {toDateText})
                </span>
              )}
              <span> {getStayDayText()}</span>
            </div>
          </div>
          {isJpe && <IconArrowDown className="ml-auto shrink-0" color="#8B9096" />}
          {!isJpe && (
            <button className="rounded-full px-3 py-1 text-white font-size-12 font-bold background-green-primary">
              {t('landing_page.search_calendar.txt_change')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full search-calendar-wrapper">
      <SearchComponent />
      <div className="hidden md:block">
        <DatePicker
          data={checkInDates}
          onChangeDataSearch={handleChangeFromDate}
          initDate={filterData.start_date ? dayjs(filterData.start_date).toDate() : undefined}
          stayDay={Number(filterData.stay_day)}
        />
        {(allergyCondition || schoolExperienceCondition) && (
          <div className="mt-4">
            <label className="text-gray-600 font-bold font-size-16">
              {t('reservation_confirm.modal_booking.storage_notice.title')}
            </label>
            {allergyCondition && (
              <p className="mt-2 text-gray-600">{t('reservation_confirm.modal_booking.storage_notice.allergy')}</p>
            )}
            {schoolExperienceCondition && (
              <p className="mt-2 text-gray-600">{t('reservation_confirm.modal_booking.storage_notice.school_experience')}</p>
            )}
          </div>
        )}
      </div>
      {openModalFilter && (
        <ModalFilterPlanByCalendar
          initDataSearch={filterData}
          onChangeDataSearch={handleChangeDataSearch}
          inputType={inputType}
        />
      )}
    </div>
  )
}
